// ── Finnish Currency Helpers ──────────────────────────────────────────────

/**
 * Formats a raw dot-decimal string (e.g. "357.94") to Finnish format ("357,94").
 * Preserves up to 2 decimal places. Unformatted input is returned as-is.
 * Thousands are not grouped: fi-FI would insert a no-break space
 * ("1 234,5"), which isValidFinnishAmount() and the input filters reject.
 */
export function toFinnishAmount(raw: string): string {
  if (!raw) return raw;
  // Already has comma – treat as Finnish
  if (raw.includes(",")) return raw;
  const n = parseFloat(raw);
  if (isNaN(n)) return raw;
  return n.toLocaleString("fi-FI", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    useGrouping: false,
  });
}

/**
 * Validates that a Finnish-formatted string is a whole number or has
 * exactly 2 decimal places separated by comma.
 * Returns true when valid.
 *
 * Allowed: "357", "357,94", "0,5"
 * Rejected: "357,9", "357,123", "abc"
 */
export function isValidFinnishAmount(value: string): boolean {
  return /^[0-9]+(,[0-9]{1,2})?$/.test(value);
}

/**
 * Converts a Finnish-formatted amount back to backend format:
 * comma → dot, always 2 decimal places.
 * e.g. "357,94" → "357.94", "357" → "357.00"
 */
export function toBackendAmount(finnish: string): string {
  const normalized = finnish.replace(",", ".");
  const n = parseFloat(normalized);
  if (isNaN(n)) return "0.00";
  return n.toFixed(2);
}

/**
 * Penalty interest amount helpers. The amount is a two-decimal string
 * on the wire with dot separator (e.g. "56.78") but shown to the
 * customer in Finnish format ("56,78"). toFinnishFloat() formats the
 * backend value (string or legacy float) for display/input;
 * toBackendAmount() converts the Finnish input back to the string sent
 * in penalty_interest.amount (null when empty). Thousands are not
 * grouped, for the same reason as in toFinnishAmount().
 */
export function toFinnishFloat(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value.toLocaleString("fi-FI", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      useGrouping: false,
    });
  }
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number.parseFloat(value.replace(",", "."));
    if (Number.isFinite(n)) {
      return n.toLocaleString("fi-FI", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        useGrouping: false,
      });
    }
  }
  return "";
}

// ── IBAN Helpers ─────────────────────────────────────────────────────────

/** Strips separators and uppercases an IBAN for validation/formatting. */
function toIbanCore(value: string): string {
  return value.replace(/[\s-]/g, "").toUpperCase();
}

/**
 * True when the value follows IBAN bank account number syntax
 * (ISO 13616): a 2-letter country code, 2 check digits and a
 * 11–30 alphanumeric BBAN (15–34 characters total), including the
 * mod-97 checksum. Spaces between groups of four are allowed, e.g.
 * "FI21 1234 5600 0007 85".
 *
 * Allowed: "FI2112345600000785", "fi21 1234 5600 0007 85"
 * Rejected: "FI00...", "XX1234567890123456789" (bad checksum), "1234"
 */
export function isValidIban(value: string): boolean {
  const iban = toIbanCore(value);
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  // ISO 13616 mod-97 check: move the BBAN to the front, append the
  // country code + check digits, expand letters to digits and verify
  // the remainder is 1. Processed in chunks to keep numbers small.
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const numeric = rearranged.replace(/[A-Z]/g, (c) =>
    String(c.charCodeAt(0) - 55),
  );
  let remainder = 0;
  for (const ch of numeric) {
    remainder = (remainder * 10 + (ch.charCodeAt(0) - 48)) % 97;
  }
  return remainder === 1;
}

/** Formats a valid IBAN in groups of four ("FI21 1234 5600 0007 85"). */
export function formatIban(value: string): string {
  return toIbanCore(value).replace(/(.{4})/g, "$1 ").trim();
}

// ── Penalty Interest Starting Date Helpers ──────────────────────────────────

/**
 * penalty_interest.starting_date helpers. The value is an ISO-8601 date
 * on the wire (e.g. "2026-12-06") but shown to the customer in Finnish
 * date format ("6.12.2026" – dots as separators, no leading zeros).
 * toFinnishDate() converts the API value for display/input; toIsoDate()
 * converts the Finnish input back for submission (null when empty or
 * invalid – same pattern as toBackendAmount() for the amount).
 */
export function toFinnishDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return iso.trim(); // Not ISO – show as-is so it stays visible
  return `${Number(m[3])}.${Number(m[2])}.${m[1]}`;
}

/** True when the value is a valid Finnish date ("6.12.2026"). */
export function isValidFinnishDate(finnish: string): boolean {
  return toIsoDate(finnish) !== null;
}

/** Converts a Finnish date ("6.12.2026") back to ISO ("2026-12-06").
 *  Returns null when empty/invalid (e.g. 31.2.2026 fails the calendar check). */
export function toIsoDate(finnish: string | null | undefined): string | null {
  if (!finnish || !finnish.trim()) return null;
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(finnish.trim());
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  const d = new Date(Date.UTC(year, month - 1, day));
  if (
    d.getUTCFullYear() !== year ||
    d.getUTCMonth() !== month - 1 ||
    d.getUTCDate() !== day
  ) {
    return null; // Not a real calendar date
  }
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

// ── Penalty Interest Rate Helpers ──────────────────────────────────────────

/**
 * Default "Viivästyskoron korkokanta" (penalty interest rate): 9,50 %.
 * Kept as a float (9.5) in the UI state and shown in a numeric input
 * whose up/down buttons step by 0,1; sent to the backend as a
 * two-decimal string (e.g. "9.50").
 */
export const DEFAULT_PI_INTEREST_RATE = 9.5;

/**
 * Parses an interest rate received from the backend (two-decimal
 * string e.g. "9.50", legacy float 9.5, or Finnish "9,50") into a
 * float for the UI state. Returns null when empty or unparseable.
 */
export function parseInterestRate(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number.parseFloat(value.replace(",", "."));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Rounds a float interest rate to one decimal (the 0,1 step size of the
 * input buttons) and clamps it at 0. E.g. 9.54 → 9.5, 9.56 → 9.6.
 */
export function roundInterestRate(n: number): number {
  return Math.max(0, Math.round(n * 10) / 10);
}

// ── Field Validators ──────────────────────────────────────────────────────

/**
 * Checks the format of a non-empty field value. Returns "" when valid,
 * else the translation key (src/i18n/locales) of the error message
 * shown under the field.
 */
export type Validator = (value: string) => string;

export const REQUIRED_MESSAGE = "validation.required";

/** Empty passes; used on blur and for optional fields. */
export function validateOptional(value: string, validate: Validator): string {
  return value.trim() ? validate(value) : "";
}

/** Empty fails; used on submit for required fields. */
export function validateRequired(value: string, validate: Validator): string {
  return value.trim() ? validate(value) : REQUIRED_MESSAGE;
}

/** Free text with no format rule; combine with validateRequired. */
export const validateText: Validator = () => "";

/** Finnish date ("6.12.2026") that exists in the calendar. */
export const validateFinnishDate: Validator = (value) =>
  isValidFinnishDate(value)
    ? ""
    : "validation.date";

/** IBAN bank account number, including the mod-97 checksum. */
export const validateIban: Validator = (value) =>
  isValidIban(value)
    ? ""
    : "validation.iban";

/** Euro amount in Finnish format: whole number or 1–2 decimals after a comma. */
export const validateFinnishAmount: Validator = (value) =>
  isValidFinnishAmount(value)
    ? ""
    : "validation.amount";

/** Interest rate in percent (dot decimal, as held in state): zero or more. */
export const validateInterestRate: Validator = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0
    ? ""
    : "validation.interestRate";
};

// Same pattern as the backend's _EMAIL_RE (backend/src/common/validation.py).
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;

/** Email address (surrounding whitespace ignored, as on the server). */
export const validateEmail: Validator = (value) =>
  EMAIL_RE.test(value.trim())
    ? ""
    : "validation.email";

/**
 * Phone number, accepted as by the backend's normalize_phone
 * (backend/src/common/validation.py): separators (space . ( ) -) are
 * ignored; international (+358…, 00358…) or Finnish national (040…)
 * input; 7–15 digits after the country prefix.
 */
export const validatePhone: Validator = (value) => {
  let phone = value.replace(/[ .()-]/g, "");
  if (phone.startsWith("00")) phone = "+" + phone.slice(2);
  else if (phone.startsWith("0")) phone = "+358" + phone.slice(1);
  return /^\+\d{7,15}$/.test(phone)
    ? ""
    : "validation.phone";
};
